<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import CmBaseCodeAutocomplete from '../../shared/CmBaseCodeAutocomplete.vue';
import type { NoticeIndividualFormModel } from './composables/useFormPayload';

const props = defineProps<{
  model: NoticeIndividualFormModel;
  readOnly?: boolean;
}>();

const emit = defineEmits<{
  (e: 'request-address-lookup', context: Record<string, unknown>): void;
}>();

const idTypeOptions = ref<Array<{ title: string; value: string }>>([]);

function mapBaseCodeOption(code: any) {
  const value = String(code.totalCode || code.TOTAL_CODE || '').trim();
  const title = String(
    code.codeName || code.CODE_NAME || code.defaultCodeName || code.DEFAULT_CODE_NAME || '',
  ).trim();
  return { title, value };
}

function isSelectableBaseCode(opt: { title: string; value: string }) {
  if (!opt.title || !opt.value) return false;
  if (opt.value.slice(-3) === '000') return false;
  const lower = opt.title.toLowerCase();
  if (lower.includes('please select') || lower === 'select') return false;
  return true;
}

function requestResidentialAddressInput() {
  if (props.readOnly) return;
  emit('request-address-lookup', {
    fieldGroup: 'controllerResidentialAddress',
    index: 0,
    addresses: [],
    directInput: true,
    subjectLabel: 'Residential address',
  });
}

function requestAlternateAddressInput() {
  if (props.readOnly) return;
  emit('request-address-lookup', {
    fieldGroup: 'controllerAlternateAddress',
    index: 0,
    addresses: [],
    directInput: true,
    subjectLabel: 'Alternate address',
  });
}

onMounted(async () => {
  try {
    const idRes = await baseCodeApi.getCodesByMainCode('CM', 'B03', undefined);
    const idCodes = (idRes as any).codes || (idRes as any).data?.codes || [];
    idTypeOptions.value = idCodes.map(mapBaseCodeOption).filter(isSelectableBaseCode);
  } catch (e) {
    console.warn('[NoticeIndividualProfileFields] B03 load failed', e);
  }
});
</script>

<template>
  <div class="ni-profile-fields">
    <v-row dense>
      <v-col cols="12" md="4">
        <v-select
          v-model="model.controllerIdTypeCd"
          :items="idTypeOptions"
          item-title="title"
          item-value="value"
          label="ID type"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :readonly="!!readOnly"
          clearable
        />
      </v-col>
      <v-col cols="12" md="4">
        <CmBaseCodeAutocomplete
          v-model="model.controllerMobileCountryCd"
          main-code="B05"
          label="Mobile country *"
          :read-only="!!readOnly"
          required-mark
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          v-model="model.controllerMobileAreaCode"
          label="Area code"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :readonly="!!readOnly"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          v-model="model.controllerContactNo"
          label="Mobile no *"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :readonly="!!readOnly"
        />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-text-field
          v-model="model.controllerResidentialAddress"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :readonly="!!readOnly"
          data-required-key="controllerResidentialAddress"
          :class="{ 'ad-required-field': true }"
        >
          <template #label>Residential address <span class="ad-required-mark">*</span></template>
          <template #append-inner>
            <v-tooltip location="top" :open-delay="150">
              <template #activator="{ props: tProps }">
                <v-btn
                  v-bind="tProps"
                  icon="mdi-creation"
                  size="x-small"
                  variant="text"
                  color="primary"
                  :disabled="!!readOnly"
                  @click.stop="requestResidentialAddressInput"
                />
              </template>
              <span>Enter address in chat</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-text-field
          v-model="model.controllerAlternateAddress"
          label="Alternate address"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :readonly="!!readOnly"
        >
          <template #append-inner>
            <v-tooltip location="top" :open-delay="150">
              <template #activator="{ props: tProps }">
                <v-btn
                  v-bind="tProps"
                  icon="mdi-creation"
                  size="x-small"
                  variant="text"
                  color="primary"
                  :disabled="!!readOnly"
                  @click.stop="requestAlternateAddressInput"
                />
              </template>
              <span>Enter address in chat</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.ni-profile-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
  margin-bottom: 20px;
}

.ni-profile-fields :deep(.v-row) {
  margin-top: 0;
  margin-bottom: 0;
}

.ni-profile-fields :deep(.v-row + .v-row) {
  margin-top: 4px;
}

.ad-required-mark {
  color: #b3261e;
}
</style>
